import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { tvKey } from './tvKeys';
import { popNav, popOk, popKey } from './tvAssets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KID_RATINGS = ['Tout public', '7+', '10+'];
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'.split('');
const KB_COLS = 6;
const SPECIALS = ['ESPACE', 'SUPPR', 'VALIDER'];

export function TvNameKeyboard({ initial, onDone, onCancel }) {
  const [val, setVal] = useState(initial);
  const [kbIdx, setKbIdx] = useState(0);

  useEffect(() => {
    const onKey = (e) => {
      const k = tvKey(e);
      if (!k) return;
      e.preventDefault();
      e.stopPropagation();
      if (k === 'back') { onCancel(); return; }
      if (k === 'ok') {
        if (kbIdx < 36) { popKey(); setVal(v => (v + LETTERS[kbIdx]).slice(0, 20)); }
        else if (kbIdx === 36) { popKey(); setVal(v => (v + ' ').slice(0, 20)); }
        else if (kbIdx === 37) { popKey(); setVal(v => v.slice(0, -1)); }
        else { popOk(); onDone(val.trim() || initial); }
        return;
      }
      let n = kbIdx;
      if (kbIdx < 36) {
        const r = Math.floor(kbIdx / KB_COLS), c = kbIdx % KB_COLS;
        if (k === 'left') n = r * KB_COLS + Math.max(0, c - 1);
        else if (k === 'right') n = r * KB_COLS + Math.min(KB_COLS - 1, c + 1);
        else if (k === 'up') n = Math.max(0, r - 1) * KB_COLS + c;
        else if (k === 'down') n = r === 5 ? 36 + Math.min(2, Math.floor(c / 2)) : (r + 1) * KB_COLS + c;
      } else {
        const s = kbIdx - 36;
        if (k === 'left') n = 36 + Math.max(0, s - 1);
        else if (k === 'right') n = 36 + Math.min(2, s + 1);
        else if (k === 'up') n = 5 * KB_COLS + s * 2;
      }
      if (n !== kbIdx) { popNav(); setKbIdx(n); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20, background: 'rgba(5,2,10,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} data-testid="tv-name-keyboard">
      <p style={{ fontSize: '1.2vw', color: 'rgba(255,255,255,0.6)', marginBottom: '1vw' }}>Nom du profil</p>
      <div style={{ minHeight: '3vw', borderBottom: '0.15vw solid rgba(255,255,255,0.3)', marginBottom: '1.8vw', minWidth: '20vw', textAlign: 'center' }} data-testid="tv-kb-value">
        <span style={{ fontSize: '1.9vw', fontWeight: 700 }}>{val}<span style={{ color: '#E9641A' }}>|</span></span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${KB_COLS}, 1fr)`, gap: '0.5vw', width: '24vw' }}>
        {LETTERS.map((l, i) => (
          <div key={l} className={`tv-btn ${kbIdx === i ? 'tv-focused' : ''}`}
            style={{ height: '3.6vw', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3vw', fontWeight: 700,
              background: kbIdx === i ? '#E9641A' : 'rgba(255,255,255,0.07)' }}
            data-testid={`tv-kb-key-${l}`}>
            {l}
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5vw', width: '24vw', marginTop: '0.5vw' }}>
        {SPECIALS.map((s, i) => (
          <div key={s} className={`tv-btn ${kbIdx === 36 + i ? 'tv-focused' : ''}`}
            style={{ padding: '0.7vw 0', textAlign: 'center', fontSize: '0.85vw', fontWeight: 700,
              background: kbIdx === 36 + i ? (s === 'VALIDER' ? '#16a34a' : '#E9641A') : 'rgba(255,255,255,0.07)' }}
            data-testid={`tv-kb-key-${s}`}>
            {s}
          </div>
        ))}
      </div>
      <p style={{ marginTop: '1.5vw', fontSize: '0.9vw', color: 'rgba(255,255,255,0.35)' }}>Touche Retour pour annuler</p>
    </div>
  );
}

export function TvProfileEdit({ profile, canDelete, onClose }) {
  const { checkAuth } = useAuth();
  const [name, setName] = useState(profile.name || '');
  const [avatarUrl, setAvatarUrl] = useState(profile.avatar_url || '');
  const [isKid, setIsKid] = useState(profile.kind === 'kids' || KID_RATINGS.includes(profile.max_age_rating));
  const [filters, setFilters] = useState(profile.default_filters || {});
  const [avatars, setAvatars] = useState([]);
  const [filterDefs, setFilterDefs] = useState([]);
  const [rowIdx, setRowIdx] = useState(0);
  const [avIdx, setAvIdx] = useState(0);
  const [typeIdx, setTypeIdx] = useState((profile.kind === 'kids' || KID_RATINGS.includes(profile.max_age_rating)) ? 1 : 0);
  const [fIdx, setFIdx] = useState(0);
  const [bIdx, setBIdx] = useState(0);
  const [kbOpen, setKbOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // null | 0 (annuler) | 1 (supprimer)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/avatar-gallery`, { withCredentials: true })
      .then(({ data }) => {
        const list = (data || []).flatMap(c => c.avatars || []);
        setAvatars(list);
        const cur = list.indexOf(profile.avatar_url);
        if (cur >= 0) setAvIdx(cur + 1);
      }).catch(() => { /* ignore */ });
    axios.get(`${API}/filters`, { withCredentials: true })
      .then(({ data }) => {
        const defs = data || [];
        setFilterDefs(defs);
        setFilters(prev => {
          const n = { ...prev };
          defs.forEach(f => { if (!(f.id in n)) n[f.id] = f.id !== 'violence'; });
          return n;
        });
      }).catch(() => { /* ignore */ });
  }, [profile]);

  const rowKeys = ['name', ...(avatars.length ? ['avatar'] : []), 'type', ...(filterDefs.length ? ['filters'] : []), 'buttons'];
  const buttons = ['save', ...(canDelete ? ['delete'] : []), 'cancel'];
  const activeRow = rowKeys[Math.min(rowIdx, rowKeys.length - 1)];

  const doSave = useCallback(async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      await axios.put(`${API}/profiles/${profile.id}`, {
        name: name.trim() || profile.name,
        avatar_url: avatarUrl || null,
        max_age_rating: isKid ? '10+' : '18+',
        kind: isKid ? 'kids' : 'adult',
        default_filters: filters,
      }, { withCredentials: true });
      await checkAuth();
      onClose();
    } catch { setError("Impossible d'enregistrer. Réessayez."); setBusy(false); }
  }, [busy, profile, name, avatarUrl, isKid, filters, checkAuth, onClose]);

  const doDelete = useCallback(async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      await axios.delete(`${API}/profiles/${profile.id}`, { withCredentials: true });
      await checkAuth();
      onClose();
    } catch { setError('Impossible de supprimer. Réessayez.'); setBusy(false); setConfirmDelete(null); }
  }, [busy, profile, checkAuth, onClose]);

  useEffect(() => {
    const onKey = (e) => {
      const k = tvKey(e);
      if (!k) return;
      if (kbOpen) return;
      e.preventDefault();
      e.stopPropagation();
      if (confirmDelete !== null) {
        if (k === 'left') { if (confirmDelete !== 0) popNav(); setConfirmDelete(0); }
        else if (k === 'right') { if (confirmDelete !== 1) popNav(); setConfirmDelete(1); }
        else if (k === 'back') setConfirmDelete(null);
        else if (k === 'ok') { popOk(); if (confirmDelete === 1) doDelete(); else setConfirmDelete(null); }
        return;
      }
      if (k === 'back') { onClose(); return; }
      if (k === 'up') { if (rowIdx > 0) popNav(); setRowIdx(r => Math.max(0, r - 1)); return; }
      if (k === 'down') { if (rowIdx < rowKeys.length - 1) popNav(); setRowIdx(r => Math.min(rowKeys.length - 1, r + 1)); return; }
      if (k === 'left' || k === 'right') {
        const d = k === 'left' ? -1 : 1;
        const cl = (v, max) => Math.max(0, Math.min(max, v));
        if (activeRow === 'avatar') { const n = cl(avIdx + d, avatars.length); if (n !== avIdx) popNav(); setAvIdx(n); }
        else if (activeRow === 'type') { const n = cl(typeIdx + d, 1); if (n !== typeIdx) popNav(); setTypeIdx(n); }
        else if (activeRow === 'filters') { const n = cl(fIdx + d, filterDefs.length - 1); if (n !== fIdx) popNav(); setFIdx(n); }
        else if (activeRow === 'buttons') { const n = cl(bIdx + d, buttons.length - 1); if (n !== bIdx) popNav(); setBIdx(n); }
        return;
      }
      if (k === 'ok') {
        popOk();
        if (activeRow === 'name') setKbOpen(true);
        else if (activeRow === 'avatar') setAvatarUrl(avIdx === 0 ? '' : avatars[avIdx - 1]);
        else if (activeRow === 'type') setIsKid(typeIdx === 1);
        else if (activeRow === 'filters') { const f = filterDefs[fIdx]; if (f) setFilters(prev => ({ ...prev, [f.id]: !prev[f.id] })); }
        else if (activeRow === 'buttons') {
          const b = buttons[bIdx];
          if (b === 'save') doSave();
          else if (b === 'delete') setConfirmDelete(0);
          else onClose();
        }
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  const labelStyle = (focused) => ({ fontSize: '1vw', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
    color: focused ? '#E9641A' : 'rgba(255,255,255,0.45)', marginBottom: '0.6vw' });
  const btnLabels = { save: busy ? 'Enregistrement…' : 'Enregistrer', delete: 'Supprimer le profil', cancel: 'Annuler' };

  return (
    <div className="tv-fade-in" style={{ height: '100%', padding: '2.5vw 6vw', overflow: 'hidden',
      background: 'linear-gradient(160deg, #150826 0%, #0b0612 55%)', display: 'flex', flexDirection: 'column' }} data-testid="tv-profile-edit">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.8vw' }}>
        <h1 style={{ fontSize: '2.2vw', fontWeight: 800 }}>Modifier le profil</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1vw' }}>
          <div style={{ width: '4vw', height: '4vw', borderRadius: '0.6vw', overflow: 'hidden', background: '#E9641A',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8vw', fontWeight: 800 }}>
            {avatarUrl ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (name || '?')[0].toUpperCase()}
          </div>
          <span style={{ fontSize: '1.3vw', fontWeight: 700 }} data-testid="tv-edit-preview-name">{name}</span>
        </div>
      </div>

      {/* Nom */}
      <div style={{ marginBottom: '1.6vw' }}>
        <p style={labelStyle(activeRow === 'name')}>Nom du profil</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.2vw' }}>
          <div className={`tv-input ${activeRow === 'name' ? 'tv-focused' : ''}`}
            style={{ padding: '0.7vw 1.2vw', fontSize: '1.25vw', fontWeight: 600, minWidth: '18vw' }} data-testid="tv-edit-name">
            {name}
          </div>
          <span style={{ fontSize: '0.9vw', color: 'rgba(255,255,255,0.35)' }}>OK pour modifier</span>
        </div>
      </div>

      {/* Avatar */}
      {avatars.length > 0 && (
        <div style={{ marginBottom: '1.6vw' }}>
          <p style={labelStyle(activeRow === 'avatar')}>Avatar</p>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: '0.8vw', transition: 'transform 0.2s ease', padding: '0.3vw 0.3vw',
              transform: `translateX(${-Math.max(0, avIdx - 8) * 5.3}vw)` }}>
              {['none', ...avatars].map((a, i) => {
                const isSel = i === 0 ? !avatarUrl : avatarUrl === a;
                const isFoc = activeRow === 'avatar' && avIdx === i;
                return (
                  <div key={i} className={`tv-btn ${isFoc ? 'tv-focused' : ''}`}
                    style={{ width: '4.5vw', height: '4.5vw', flex: 'none', borderRadius: '50%', overflow: 'hidden', position: 'relative',
                      border: isSel ? '0.2vw solid #E9641A' : '0.2vw solid transparent',
                      background: i === 0 ? '#E9641A' : 'rgba(255,255,255,0.08)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    data-testid={`tv-edit-avatar-${i}`}>
                    {i === 0
                      ? <span style={{ fontSize: '1.6vw', fontWeight: 800 }}>{(name || '?')[0].toUpperCase()}</span>
                      : <img src={a} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    {isSel && (
                      <div style={{ position: 'absolute', bottom: 0, right: 0, background: '#E9641A', borderRadius: '50%', width: '1.3vw', height: '1.3vw', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Check style={{ width: '0.8vw', height: '0.8vw' }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Type de profil */}
      <div style={{ marginBottom: '1.6vw' }}>
        <p style={labelStyle(activeRow === 'type')}>Type de profil</p>
        <div style={{ display: 'flex', gap: '0.8vw' }}>
          {['Adulte', 'Enfant'].map((t, i) => {
            const isSel = (i === 1) === isKid;
            const isFoc = activeRow === 'type' && typeIdx === i;
            return (
              <div key={t} className={`tv-btn ${isFoc ? 'tv-focused' : ''}`}
                style={{ padding: '0.6vw 1.8vw', fontSize: '1.1vw', fontWeight: 700, borderRadius: '2vw',
                  background: isSel ? '#E9641A' : 'rgba(255,255,255,0.08)' }}
                data-testid={`tv-edit-type-${i === 0 ? 'adult' : 'kid'}`}>
                {isSel && <Check style={{ width: '1vw', height: '1vw', display: 'inline', verticalAlign: '-0.15vw', marginRight: '0.4vw' }} />}
                {t}
              </div>
            );
          })}
        </div>
        <p style={{ fontSize: '0.85vw', color: 'rgba(255,255,255,0.35)', marginTop: '0.5vw' }}>
          Profil enfant : seuls les titres adaptés (10 ans et moins) sont visibles
        </p>
      </div>

      {/* Filtres par défaut */}
      {filterDefs.length > 0 && (
        <div style={{ marginBottom: '2vw' }}>
          <p style={labelStyle(activeRow === 'filters')}>Filtres de contenu par défaut</p>
          <div style={{ display: 'flex', gap: '0.7vw', flexWrap: 'wrap' }}>
            {filterDefs.map((f, i) => {
              const on = !!filters[f.id];
              const isFoc = activeRow === 'filters' && fIdx === i;
              return (
                <div key={f.id} className={`tv-btn ${isFoc ? 'tv-focused' : ''}`}
                  style={{ padding: '0.55vw 1.3vw', fontSize: '1vw', fontWeight: 600, borderRadius: '2vw',
                    background: on ? '#E9641A' : 'rgba(255,255,255,0.08)', color: on ? '#fff' : 'rgba(255,255,255,0.7)' }}
                  data-testid={`tv-edit-filter-${f.id}`}>
                  {on && <Check style={{ width: '0.9vw', height: '0.9vw', display: 'inline', verticalAlign: '-0.12vw', marginRight: '0.4vw' }} />}
                  {f.name}
                </div>
              );
            })}
          </div>
          <p style={{ fontSize: '0.85vw', color: 'rgba(255,255,255,0.35)', marginTop: '0.5vw' }}>OK pour activer/désactiver un filtre</p>
        </div>
      )}

      {/* Boutons */}
      <div style={{ display: 'flex', gap: '1vw', marginTop: 'auto', paddingBottom: '1vw' }}>
        {buttons.map((b, i) => {
          const isFoc = activeRow === 'buttons' && bIdx === i;
          const danger = b === 'delete';
          return (
            <div key={b} className={`tv-btn ${isFoc ? 'tv-focused' : ''}`}
              style={{ padding: '0.8vw 2vw', fontSize: '1.15vw', fontWeight: 700, borderRadius: '0.5vw',
                background: b === 'save' ? (isFoc ? '#E9641A' : 'rgba(233,100,26,0.75)') : danger ? (isFoc ? '#b91c1c' : 'rgba(185,28,28,0.4)') : 'rgba(255,255,255,0.1)' }}
              data-testid={`tv-edit-${b}`}>
              {btnLabels[b]}
            </div>
          );
        })}
        {error && <p style={{ color: '#f87171', fontSize: '1vw', alignSelf: 'center' }} data-testid="tv-edit-error">{error}</p>}
      </div>

      {kbOpen && <TvNameKeyboard initial={name} onDone={(v) => { setName(v); setKbOpen(false); }} onCancel={() => setKbOpen(false)} />}

      {confirmDelete !== null && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20, background: 'rgba(5,2,10,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} data-testid="tv-delete-confirm">
          <p style={{ fontSize: '1.6vw', fontWeight: 700, marginBottom: '0.8vw' }}>Supprimer le profil « {profile.name} » ?</p>
          <p style={{ fontSize: '1vw', color: 'rgba(255,255,255,0.5)', marginBottom: '2vw' }}>L'historique et les préférences de ce profil seront perdus.</p>
          <div style={{ display: 'flex', gap: '1vw' }}>
            <div className={`tv-btn ${confirmDelete === 0 ? 'tv-focused' : ''}`}
              style={{ padding: '0.8vw 2.2vw', fontSize: '1.15vw', fontWeight: 700, background: 'rgba(255,255,255,0.1)' }}
              data-testid="tv-delete-cancel">Annuler</div>
            <div className={`tv-btn ${confirmDelete === 1 ? 'tv-focused' : ''}`}
              style={{ padding: '0.8vw 2.2vw', fontSize: '1.15vw', fontWeight: 700, background: confirmDelete === 1 ? '#b91c1c' : 'rgba(185,28,28,0.5)' }}
              data-testid="tv-delete-confirm-btn">{busy ? 'Suppression…' : 'Supprimer'}</div>
          </div>
        </div>
      )}
    </div>
  );
}
